// Voice channel switch confirmation modal — "You can only be in one voice
// channel at a time". Extracted verbatim from ChatLayout's inline render
// (step 7.6.12). Structural props only — no imports back into ChatLayout
// (refactor rule 5).
//
// Presentational: the parent keeps the `voiceSwitchPrompt` gate and derives the
// display names from its channels/voids lists (the channels read is spine-
// coupled → stays parent-side per 7.3), then passes resolved strings in. The
// spine-coupled actions collapse to onCancel (setVoiceSwitchPrompt(null)) and
// onConfirmSwitch (confirmVoiceSwitch).
//
// Static-verify only: LiveKit is not configured in the dev env, so this move is
// proven by tsc + vite build + line-by-line diff audit, not a live call.
import { useRef } from 'react';
import { useDialogA11y } from '../../hooks/useDialogA11y';

type VoiceSwitchModalProps = {
  /** Display name of the channel being joined. */
  targetChannelName: string;
  /** Display name of the space being joined (shown only when isSameSpace is false). */
  targetSpaceName: string;
  /** Display name of the channel currently on call. */
  activeChannelName: string;
  /** Display name of the space currently on call (shown only when isSameSpace is false). */
  activeSpaceName: string;
  isSameSpace: boolean;
  onCancel: () => void;
  onConfirmSwitch: () => void;
};

export default function VoiceSwitchModal({
  targetChannelName,
  targetSpaceName,
  activeChannelName,
  activeSpaceName,
  isSameSpace,
  onCancel,
  onConfirmSwitch,
}: VoiceSwitchModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(true, dialogRef, onCancel);
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="voice-switch-title" tabIndex={-1} className="w-full max-w-sm rounded-2xl border border-neutral-700 bg-neutral-900 p-6 shadow-2xl mx-4 outline-none">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-emerald-400"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
          </div>
          <div>
            <div id="voice-switch-title" className="font-medium text-fg">Switch voice channel?</div>
            <div className="text-xs text-fg-2 mt-0.5">You can only be in one voice channel at a time</div>
          </div>
        </div>
        <div className="rounded-xl bg-neutral-800/60 border border-neutral-700 p-3 mb-5 space-y-2 text-sm">
          <div className="flex items-center gap-2 text-fg-2">
            <span className="text-red-400/80 text-xs font-medium">Leave</span>
            <span className="text-fg font-medium">#{activeChannelName}</span>
            {!isSameSpace && <span className="text-fg-3">in {activeSpaceName}</span>}
          </div>
          <div className="flex items-center gap-2 text-fg-2">
            <span className="text-emerald-400/80 text-xs font-medium">Join</span>
            <span className="text-fg font-medium">#{targetChannelName}</span>
            {!isSameSpace && <span className="text-fg-3">in {targetSpaceName}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            className="flex-1 px-4 py-2 rounded-lg border border-neutral-700 text-fg-2 hover:bg-neutral-800/60 text-sm transition-colors"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="flex-1 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors"
            onClick={onConfirmSwitch}
          >
            Disconnect &amp; Join
          </button>
        </div>
      </div>
    </div>
  );
}
